import template from 'lodash/template';
import preRehypeReact from 'pre-rehype-react';
import T from 'prop-types';
import React, { useEffect, useState } from 'react';
import rehype2react from 'rehype-react';
import remark from 'remark';
import remarkBreaks from 'remark-breaks';
import gfm from 'remark-gfm';
import remark2rehype from 'remark-rehype';
import * as remarkReactComponents from './MarkdownMappings';
import styles from './style.css';

/*
  remark-attr v0.11.0 (https://github.com/arobase-che/remark-attr) is not compatible with remark v13
  todo: keep an eye on https://github.com/remarkjs/remark/blob/main/doc/plugins.md
*/

const withParentProps = (WrappedComponent, componentProps) => {
  class HOC extends React.Component {
    render() {
      return <WrappedComponent {...this.props} {...componentProps} />;
    }
  }
  return HOC;
};

export const wrapComponents = (components, componentsProps) =>
  Object.keys(components).reduce((acc, key) => {
    acc[key] = withParentProps(components[key], componentsProps);
    return acc;
  }, {});

const compileMarkdown = async (interpolatedMarkdown, componentsProps, cb) => {
  try {
    const { result } = await remark()
      .use(remarkBreaks)
      .use(gfm)
      .use(remark2rehype)
      .use(preRehypeReact, {
        components: ['callout', 'expander', 'reveal', 'section'],
        environment: global.__DEV__ ? 'development' : 'production',
      })
      .use(rehype2react, {
        createElement: React.createElement,
        components: wrapComponents(remarkReactComponents, componentsProps),
      })
      .process(interpolatedMarkdown);

    cb(null, result);
  } catch (err) {
    if (global.__DEV__) {
      // eslint-disable-next-line
      console.error(err);
    }

    cb('There has been an error');
  }
};

const Markdown = ({
  children,
  'data-testid': dataTestId = 'markdown',
  mappedComponentsProps = {},
  markdownVars = {},
  noBottomMargin = false,
}) => {
  const [markdown, setMarkdown] = useState(null);
  const [error, setError] = useState(null);
  const interpolatedMarkdown = template(children)(markdownVars);

  useEffect(() => {
    let mounted = true;

    compileMarkdown(
      interpolatedMarkdown,
      mappedComponentsProps,
      (err, contents) => {
        if (mounted) {
          if (err) {
            return setError(err);
          }

          setMarkdown(contents);
        }
      }
    );

    return () => (mounted = false);
  }, [interpolatedMarkdown]);

  const markdownClassNames = [styles.markdown];

  if (noBottomMargin) {
    markdownClassNames.push(styles.noBottomMargin);
  }

  return (
    error || (
      <div className={markdownClassNames.join(' ')} data-testid={dataTestId}>
        {markdown}
      </div>
    )
  );
};

Markdown.propTypes = {
  children: T.oneOfType([T.arrayOf(T.node), T.node]).isRequired,
  'data-testid': T.string,
  mappedComponentsProps: T.object,
  markdownVars: T.object,
  noBottomMargin: T.bool,
};

export default Markdown;
